import React, { useState } from 'react';
import axios from 'axios';
import { Buffer } from 'buffer';
import { useTranslation } from 'react-i18next';

import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ActionButtons } from '../../others/ActionButtons';
import LoadingOverlay from '../../others/LoadingOverlay';
import DynamicForm from '../../dynamicForm/DynamicForm';

import { useValidateDynamicForm } from '../../../hooks/useValidateDynamicForm';
import { useNotification } from '../../../hooks/useNotification';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useApiResolver } from '../../../hooks/useApiResolver';

import { isNotEmptyObject } from '../../../utils/jsonValidator';
import { ActionIconItem } from '../../others/ActionIconItem';
import { useForm } from '../../../hooks/useForm';
import { useResolveBodyData } from '../../../hooks/useResolveBodyData';
import { useEffect } from 'react';

const baseUrl = process.env.REACT_APP_AWS_API_GATEWAY_URL_EXECUTE_FLOW;

export const ModalForms = ({ data, dtoReg, handleChange, companyInfo = {} }) => {
    const { t } = useTranslation();
    const { resolveValue } = useApiResolver();
    const [{ userSession }] = useDataAuth();
    const [errorInputs, validForm, resetErrors] = useValidateDynamicForm();
    const [sendNotification] = useNotification();
    const [open, setOpen] = useState(false);
    const [isLoading, setisLoading] = useState(false);
    const [fileBytes, setFileBytes] = useState(null);
    const [file, setFile] = useState(null);

    const theme = useTheme();
    useMediaQuery(theme.breakpoints.down('sm'));

    const { bodyData, resolveInfo } = useResolveBodyData(data.trigger[0].bodyData);
    const [formData, handleInputChange, handleDirectInputChange] = useForm(
        data?.form?.reduce((json, element) => {
            if (dtoReg.hasOwnProperty(element.id)) {
                json[element.id] = (dtoReg[element.id]).toString();
            }
            return json;
        }, { id: dtoReg.id }) || { id: dtoReg.id }
    );

    useEffect(() => {
        resolveInfo(companyInfo);
    }, [companyInfo])

    const setValuesSelectGeneric = (e) => {
        const { name, value, fullObject } = e.target;
        handleDirectInputChange(name, value);
        if (isNotEmptyObject(fullObject)) {
            for (let key in fullObject) {
                const left = `${bodyData[key]}`;
                const right = `{${name}_${key}}`;
                if (left === right) {
                    handleDirectInputChange(`${name}_${key}`, fullObject[key]);
                }
            }
            // resolveInfo(fullObject, name);
        }
    }

    const setFileTemp = (bytes, field) => {
        const u8 = new Uint8Array(bytes);
        const b64 = Buffer.from(u8).toString('base64');
        handleDirectInputChange(field + '_file_B64', b64);
        setFileBytes(bytes);
    };

    const handleFileChange = (event, field) => {
        const fileSelected = event.target.files[0];
        setFile(fileSelected);
        handleDirectInputChange(field, fileSelected?.name);

        if (fileSelected) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const bytes = new Uint8Array(e.target.result);
                setFileTemp(bytes, field);
            };
            reader.readAsArrayBuffer(fileSelected);
        }
        event.target.value = '';
    };

    const handleUploadClick = async () => {
        if (!fileBytes) return;

        try {
            const u8 = new Uint8Array(fileBytes);
            const b64 = Buffer.from(u8).toString('base64');

            const bodyData = {
                id_flow: "260",
                id: dtoReg.id,
                name: file.name.replace(/\s+/g, '_'),
                file: b64
            };

            const response = await axios.post(baseUrl, bodyData, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userSession.token}`
                },
            });

            if (response.status === 200) {
                Object.keys(response.data).forEach((key) => {
                    formData[key] = response.data[key];
                });
                sendNotification({ msg: t("messages.fileUploadSuccess"), variant: 'info' });
            } else {
                sendNotification({ msg: t("messages.saveError"), variant: 'error' });
            }
        } catch (error) {
            sendNotification({ msg: t("messages.saveError"), variant: 'error' });
        }
    };

    const CallApi = async (param) => {
        if (!validForm(data.form, formData)) return;

        setisLoading(true);
        setOpen(false);

        const { url, method } = param[0];
        const body = resolveInfo(formData);
        if (companyInfo.id_company) {
            body.id_company = companyInfo.id_company;
            body.select_company = companyInfo.select_company;
        }
        console.log('body_resuelto', body);
        let str = JSON.stringify(body);

        for (let key in formData) {
            let value = (formData[key] || "").toString().replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ');
            if (value === `{${key}}`)
                value = ''
            str = str.replace(`{${key}}`, value).replace(`{[form].${key}}`, value);
        }
        const { success } = await resolveValue({
            method,
            url,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userSession.token}`,
            },
            data: JSON.stringify(body),
        });
        if (success)
            setTimeout(() => handleChange(), 500);
        setisLoading(false);
    };

    return (
        <>
            <ActionIconItem
                item={data}
                onClick={() => {
                    setOpen(true);
                    resetErrors();
                }}
                defaultIcon="DynamicForm"
                badgeContent={data?.badge || null}
            />

            <Dialog
                open={open}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        width: {
                            xs: '100%',
                            md: data?.width ? `${data.width}%` : '35%'
                        },
                        margin: { xs: 1, md: 0 },
                        maxHeight: '90vh',
                        borderRadius: 2,
                    }
                }}
            >
                {/* ---------- HEADER ---------- */}
                <DialogTitle sx={{ borderBottom: '1px solid #ccc' }}>
                    <Typography variant="h6">{data.accion}</Typography>
                </DialogTitle>

                {/* ---------- BODY ---------- */}
                <DialogContent sx={{ overflowY: 'auto', flexGrow: 1 }}>
                    <DynamicForm
                        values={formData}
                        scheme={data.form}
                        errorInputs={errorInputs}
                        onChange={handleInputChange}
                        handleFileChange={handleFileChange}
                        handleUploadClick={handleUploadClick}
                        setChanges={handleDirectInputChange}
                        setValuesSelectGeneric={setValuesSelectGeneric}
                        companyInfo={companyInfo}
                    />
                </DialogContent>

                {/* ---------- FOOTER ---------- */}
                <DialogActions sx={{ borderTop: '1px solid #ccc' }}>
                    <ActionButtons
                        onSubmit={() => CallApi(data.trigger)}
                        onCancel={() => setOpen(false)}
                    />
                </DialogActions>
            </Dialog>

            <LoadingOverlay isOpen={isLoading} />
        </>
    );
};
